"use client";

import { Suspense, lazy, useEffect, useState, memo } from "react";
import { Helmet } from "react-helmet-async";
import { useLenis } from "@/hooks/use-lenis";
import { HeaderNav } from "@/components/HeaderNav";
import { HeroSection } from "@/components/HeroSection";
import { LogoMarquee } from "@/components/LogoMarquee";
import { CookieConsent } from "@/components/CookieConsent";
import { Footer } from "@/components/Footer";
import { SectionSkeleton } from "@/components/ui/SectionSkeleton";
import { LanguageProvider, useLanguage } from "@/contexts/LanguageContext";
import { useIsMobile } from "@/hooks/use-mobile";

// Lazy load heavy sections for better initial performance
const SatisfiedClientsSection = lazy(() => import("@/components/SatisfiedClientsSection"));
const BentoSection = lazy(() => import("@/components/BentoSection"));
const PortfolioSection = lazy(() => import("@/components/PortfolioSection"));
const TimelineSection = lazy(() => import("@/components/TimelineSection"));
const OffersSection = lazy(() => import("@/components/OffersSection"));
const FAQSection = lazy(() => import("@/components/FAQSection"));
const CTASection = lazy(() => import("@/components/CTASection"));

// Lazy load non-essential components after idle
const FloatingParticles = lazy(() => import("@/components/FloatingParticles").then(m => ({ default: m.FloatingParticles })));
const CursorTrail = lazy(() => import("@/components/CursorTrail").then(m => ({ default: m.CursorTrail })));

const seoContent = {
  fr: {
    title: "Azurys - Votre site web en 4 jours | Agence web Paris",
    description: "Azurys crée votre site web professionnel en 4 jours. Agence web à Paris spécialisée dans la création de sites modernes, performants et sur-mesure.",
    ogTitle: "Azurys - Votre site web en 4 jours",
    ogDescription: "L'agence qui révolutionne le web design. Sites modernes, performants et livrés en 4 jours."
  },
  en: {
    title: "Azurys - Your website in 4 days | Web Agency Paris",
    description: "Azurys creates your professional website in 4 days. Paris-based web agency specializing in modern, high-performance, custom websites.",
    ogTitle: "Azurys - Your website in 4 days",
    ogDescription: "The agency revolutionizing web design. Modern, high-performance websites delivered in 4 days."
  }
};

const siteUrl = "https://www.azurys.fr";

const IndexContent = memo(function IndexContent() {
  useLenis();
  const { lang } = useLanguage();
  const seo = seoContent[lang];
  const isMobile = useIsMobile();
  const [showEffects, setShowEffects] = useState(false);

  // Load visual effects after idle - desktop only
  useEffect(() => {
    if (isMobile) return;
    
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => setShowEffects(true), { timeout: 3000 });
    } else {
      setTimeout(() => setShowEffects(true), 2000);
    }
  }, [isMobile]);

  return (
    <>
      <Helmet>
        <html lang={lang} />
        <title>{seo.title}</title>
        <meta name="description" content={seo.description} />
        <link rel="canonical" href={siteUrl} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={siteUrl} />
        <meta property="og:title" content={seo.ogTitle} />
        <meta property="og:description" content={seo.ogDescription} />
        <meta property="og:image" content={`${siteUrl}/og-image.jpg`} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="Azurys" />
        <meta property="og:locale" content={lang === "fr" ? "fr_FR" : "en_US"} />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={siteUrl} />
        <meta name="twitter:title" content={seo.ogTitle} />
        <meta name="twitter:description" content={seo.ogDescription} />
        <meta name="twitter:image" content={`${siteUrl}/og-image.jpg`} />
      </Helmet>
      
      <div className="relative min-h-screen bg-background">
        {/* Defer visual effects - desktop only */}
        {showEffects && !isMobile && (
          <>
            <Suspense fallback={null}>
              <CursorTrail />
            </Suspense>
            <div className="fixed inset-0 pointer-events-none z-0">
              <Suspense fallback={null}>
                <FloatingParticles />
              </Suspense>
            </div>
          </>
        )}

        <HeaderNav />

        <main className="relative z-10">
          <HeroSection />
          <LogoMarquee />
          
          <Suspense fallback={<SectionSkeleton />}>
            <SatisfiedClientsSection />
          </Suspense>
          
          <Suspense fallback={<SectionSkeleton />}>
            <BentoSection />
          </Suspense>
          
          <Suspense fallback={<SectionSkeleton />}>
            <PortfolioSection />
          </Suspense>
          
          <Suspense fallback={<SectionSkeleton />}>
            <TimelineSection />
          </Suspense>
          
          <Suspense fallback={<SectionSkeleton />}>
            <OffersSection />
          </Suspense>
          
          <Suspense fallback={<SectionSkeleton />}>
            <FAQSection />
          </Suspense>
          
          <Suspense fallback={<SectionSkeleton height="min-h-[600px]" />}>
            <CTASection />
          </Suspense>
        </main>

        <Footer />
        <CookieConsent />
      </div>
    </>
  );
});

const Index = () => {
  return (
    <LanguageProvider>
      <IndexContent />
    </LanguageProvider>
  );
};

export default Index;
